import { useMemo } from 'react';
import { formatCurrency } from '../../../../utils/formatters';
import { PaymentConfig, OrderSummary } from '../../../../types/sales.types';
import { getPaymentMethodsDisplayLabel } from '../../../../utils/sales/paymentMethodDisplay';

interface PaymentMethodOption {
  id: string;
  name: string;
  icon: any;
  color: string;
}

export interface UsePaymentInformationCardProps {
  paymentConfig: PaymentConfig;
  orderSummary: OrderSummary;
  paymentMethodOptions: PaymentMethodOption[];
}

export const usePaymentInformationCard = ({
  paymentConfig,
  orderSummary,
  paymentMethodOptions,
}: UsePaymentInformationCardProps) => {
  // Formateo de montos
  const formattedCashAmount = useMemo(
    () => formatCurrency(paymentConfig.cashAmount),
    [paymentConfig.cashAmount],
  );

  const formattedChange = useMemo(() => formatCurrency(orderSummary.change), [orderSummary.change]);

  const formattedPendingBalance = useMemo(
    () => formatCurrency(orderSummary.pendingBalance),
    [orderSummary.pendingBalance],
  );

  const paymentMethodName = useMemo(
    () => getPaymentMethodsDisplayLabel(paymentConfig, paymentMethodOptions),
    [paymentConfig, paymentMethodOptions],
  );

  // Texto del estado de pago parcial
  const partialPaymentStatusText = useMemo(() => {
    return paymentConfig.allowPartialPayment ? 'Habilitado' : 'Deshabilitado';
  }, [paymentConfig.allowPartialPayment]);

  // Color del texto de pago parcial
  const partialPaymentColor = useMemo(() => {
    return paymentConfig.allowPartialPayment ? 'success.main' : 'text.secondary';
  }, [paymentConfig.allowPartialPayment]);

  // Flags condicionales para mostrar/ocultar secciones
  const showCashInfo = paymentConfig.cashAmount > 0;
  const showChange = orderSummary.change > 0;
  const showPendingBalance = orderSummary.pendingBalance > 0;
  const isPartialPayment = orderSummary.paymentStatus === 'partial';
  const hasRegisteredPayments = paymentConfig.payments.length > 0;

  const resolvePaymentMethodName = (methodId: string) =>
    paymentMethodOptions.find((m) => m.id === methodId)?.name ?? methodId;

  return {
    formattedCashAmount,
    formattedChange,
    formattedPendingBalance,
    paymentMethodName,
    partialPaymentStatusText,
    partialPaymentColor,
    showCashInfo,
    showChange,
    showPendingBalance,
    isPartialPayment,
    hasRegisteredPayments,
    resolvePaymentMethodName,
  };
};
